(() => {
  const CUSTOM_ELEMENT_NAME = 'ag-faq';
  const ITEM_SELECTOR = '.ag-faq__item';
  const SUMMARY_SELECTOR = '.ag-faq__summary';
  const PANEL_SELECTOR = '.ag-faq__panel';
  const SINGLE_OPEN_ATTRIBUTE = 'data-single-open';
  const EXPANDED_CLASS = 'is-expanded';
  const ANIMATING_DATA_KEY = 'agFaqAnimating';
  const HEIGHT_TRANSITION_PROPERTY = 'height';
  const COLLAPSED_HEIGHT = '0px';
  const EXPANDED_HEIGHT = 'auto';
  const HIDDEN_OPACITY = '0';
  const VISIBLE_OPACITY = '1';
  const ANIMATION_FALLBACK_DELAY = 460;
  const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
  const ESCAPE_KEY = 'Escape';

  class AgFaq extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.itemBySummary = new Map();
      this.itemByDetails = new Map();
      this.isInitialized = false;

      this.handleSummaryClick = this.handleSummaryClick.bind(this);
      this.handleItemKeydown = this.handleItemKeydown.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.isInitialized = true;
      this.items = Array.from(this.querySelectorAll(ITEM_SELECTOR)).reduce((items, details) => {
        const summary = details.querySelector(SUMMARY_SELECTOR);
        const panel = details.querySelector(PANEL_SELECTOR);

        if (!summary || !panel) {
          return items;
        }

        const item = {
          details,
          summary,
          panel,
          finishAnimation: null,
          transitionEndHandler: null,
          timeoutId: null,
          frameIds: [],
          targetOpen: null,
        };

        details.classList.toggle(EXPANDED_CLASS, details.open);
        panel.style.height = details.open ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT;
        panel.style.opacity = details.open ? VISIBLE_OPACITY : HIDDEN_OPACITY;
        details.dataset[ANIMATING_DATA_KEY] = 'false';

        summary.addEventListener('click', this.handleSummaryClick);
        details.addEventListener('keydown', this.handleItemKeydown);
        this.itemBySummary.set(summary, item);
        this.itemByDetails.set(details, item);
        items.push(item);
        return items;
      }, []);
    }

    disconnectedCallback() {
      if (!this.isInitialized) {
        return;
      }

      this.items.forEach((item) => {
        item.summary.removeEventListener('click', this.handleSummaryClick);
        item.details.removeEventListener('keydown', this.handleItemKeydown);
        this.cancelAnimation(item, true);
      });

      this.items = [];
      this.itemBySummary.clear();
      this.itemByDetails.clear();
      this.isInitialized = false;
    }

    handleSummaryClick(event) {
      event.preventDefault();

      const item = this.itemBySummary.get(event.currentTarget);
      if (!item || item.details.dataset[ANIMATING_DATA_KEY] === 'true') {
        return;
      }

      const targetOpen = !item.details.open;
      const reducedMotion = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches;

      if (targetOpen && this.hasAttribute(SINGLE_OPEN_ATTRIBUTE)) {
        this.closeOtherItems(item, reducedMotion);
      }

      this.setItemOpen(item, targetOpen, reducedMotion);
    }

    handleItemKeydown(event) {
      if (event.key !== ESCAPE_KEY) {
        return;
      }

      const item = this.itemByDetails.get(event.currentTarget);
      if (!item) {
        return;
      }

      if (item.details.dataset[ANIMATING_DATA_KEY] === 'true') {
        this.cancelAnimation(item, true);
      }

      if (!item.details.open) {
        return;
      }

      event.preventDefault();
      this.setItemOpen(item, false, window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches);
      item.summary.focus();
    }

    closeOtherItems(currentItem, reducedMotion) {
      this.items.forEach((item) => {
        if (item === currentItem) {
          return;
        }

        if (item.details.dataset[ANIMATING_DATA_KEY] === 'true') {
          this.cancelAnimation(item, true);
        }

        if (item.details.open) {
          this.setItemOpen(item, false, reducedMotion);
        }
      });
    }

    setItemOpen(item, targetOpen, reducedMotion) {
      if (reducedMotion) {
        this.cancelAnimation(item, false);
        item.details.open = targetOpen;
        item.details.classList.toggle(EXPANDED_CLASS, targetOpen);
        item.panel.style.height = targetOpen ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT;
        item.panel.style.opacity = targetOpen ? VISIBLE_OPACITY : HIDDEN_OPACITY;
        return;
      }

      item.details.dataset[ANIMATING_DATA_KEY] = 'true';

      if (!targetOpen) {
        this.collapse(item);
        return;
      }

      this.expand(item);
    }

    collapse(item) {
      item.targetOpen = false;
      item.details.classList.remove(EXPANDED_CLASS);
      item.panel.style.height = `${item.panel.scrollHeight}px`;
      item.panel.style.opacity = VISIBLE_OPACITY;

      this.nextFrame(item, () => {
        item.panel.style.height = COLLAPSED_HEIGHT;
        item.panel.style.opacity = HIDDEN_OPACITY;
      });

      this.finishAnimation(item, () => {
        item.details.open = false;
      });
    }

    expand(item) {
      item.targetOpen = true;
      item.details.open = true;
      item.details.classList.add(EXPANDED_CLASS);
      item.panel.style.height = COLLAPSED_HEIGHT;
      item.panel.style.opacity = HIDDEN_OPACITY;

      this.nextFrame(item, () => {
        item.panel.style.height = `${item.panel.scrollHeight}px`;
        item.panel.style.opacity = VISIBLE_OPACITY;
      });

      this.finishAnimation(item, () => {
        item.panel.style.height = EXPANDED_HEIGHT;
      });
    }

    nextFrame(item, callback) {
      const firstFrameId = window.requestAnimationFrame(() => {
        this.removeFrameId(item, firstFrameId);

        const secondFrameId = window.requestAnimationFrame(() => {
          this.removeFrameId(item, secondFrameId);
          callback();
        });

        item.frameIds.push(secondFrameId);
      });

      item.frameIds.push(firstFrameId);
    }

    removeFrameId(item, frameId) {
      item.frameIds = item.frameIds.filter((currentFrameId) => currentFrameId !== frameId);
    }

    finishAnimation(item, callback) {
      let completed = false;

      const finish = () => {
        if (completed) {
          return;
        }

        completed = true;
        item.panel.removeEventListener('transitionend', onTransitionEnd);
        if (item.timeoutId !== null) {
          window.clearTimeout(item.timeoutId);
          item.timeoutId = null;
        }
        item.finishAnimation = null;
        item.transitionEndHandler = null;
        item.details.dataset[ANIMATING_DATA_KEY] = 'false';
        callback();
        item.targetOpen = null;
      };

      const onTransitionEnd = (transitionEvent) => {
        if (transitionEvent.propertyName === HEIGHT_TRANSITION_PROPERTY) {
          finish();
        }
      };

      item.finishAnimation = finish;
      item.transitionEndHandler = onTransitionEnd;
      item.panel.addEventListener('transitionend', onTransitionEnd);
      item.timeoutId = window.setTimeout(finish, ANIMATION_FALLBACK_DELAY);
    }

    cancelAnimation(item, settleState) {
      item.frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
      item.frameIds = [];

      if (item.transitionEndHandler) {
        item.panel.removeEventListener('transitionend', item.transitionEndHandler);
        item.transitionEndHandler = null;
      }
      item.finishAnimation = null;

      if (item.timeoutId !== null) {
        window.clearTimeout(item.timeoutId);
        item.timeoutId = null;
      }

      if (settleState && item.targetOpen !== null) {
        item.details.open = item.targetOpen;
        item.details.classList.toggle(EXPANDED_CLASS, item.targetOpen);
        item.panel.style.height = item.targetOpen ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT;
        item.panel.style.opacity = item.targetOpen ? VISIBLE_OPACITY : HIDDEN_OPACITY;
      }

      item.targetOpen = null;
      item.details.dataset[ANIMATING_DATA_KEY] = 'false';
    }
  }

  if (!customElements.get(CUSTOM_ELEMENT_NAME)) {
    customElements.define(CUSTOM_ELEMENT_NAME, AgFaq);
  }
})();
